import { useState } from "react";
import { useNotification } from "../../../context/notification-provider";
import {
  saveReplacementCharacter,
  updateReplacementCharacter,
  deleteReplacementCharacter,
} from "../services/template-service";
import type { ReplacementCharacterItem } from "../types/api-types";
import { getCharFromCode, getCodeFromChar } from "./utils";

interface UseReplacementCharacterManagementProps {
  templateId: number;
  characters: ReplacementCharacterItem[];
  mutate: () => void;
}

const useReplacementCharacterManagement = ({
  templateId,
  characters,
  mutate,
}: UseReplacementCharacterManagementProps) => {
  const { showNotification } = useNotification();

  // Form dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [value, setValue] = useState("");
  const [replacement, setReplacement] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editId, setEditId] = useState<number>(0);

  // Delete dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setValue("");
    setReplacement("");
    setEditIndex(null);
    setEditId(0);
  };

  const handleOpenEditDialog = (
    index: number,
    item: ReplacementCharacterItem,
  ) => {
    setOpenDialog(true);
    setValue(getCharFromCode(item.asciiValue));
    setReplacement(getCharFromCode(item.replacementValue));
    setEditIndex(index);
    setEditId(item.id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setValue("");
    setReplacement("");
    setEditIndex(null);
    setEditId(0);
  };

  const handleSave = async () => {
    // Validate that both are exactly one character
    if (value.length !== 1 || replacement.length !== 1) {
      return;
    }

    try {
      const requestData = {
        id: editId,
        templateId,
        asciiValue: getCodeFromChar(value),
        replacementValue: getCodeFromChar(replacement),
      };

      if (editIndex === null) {
        await saveReplacementCharacter(requestData);
        showNotification({
          message: "Replacement character created successfully",
          severity: "success",
        });
      } else {
        await updateReplacementCharacter(requestData);
        showNotification({
          message: "Replacement character updated successfully",
          severity: "success",
        });
      }

      mutate();
      handleCloseDialog();
    } catch {
      showNotification({
        message: `Failed to ${editIndex === null ? "create" : "update"} replacement character`,
        severity: "error",
      });
    }
  };

  const handleRemove = (index: number) => {
    setIndexToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (indexToDelete === null) return;

    const item = characters[indexToDelete];
    if (!item) return;

    setIsDeleting(true);

    try {
      await deleteReplacementCharacter(item.id);
      mutate();
      showNotification({
        message: "Replacement character deleted successfully",
        severity: "success",
      });
    } catch {
      showNotification({
        message: "Failed to delete replacement character",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setIndexToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setIndexToDelete(null);
  };

  return {
    // Form dialog
    openDialog,
    value,
    replacement,
    editIndex,
    setValue,
    setReplacement,
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleSave,

    // Delete dialog
    deleteConfirmOpen,
    indexToDelete,
    isDeleting,
    handleRemove,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useReplacementCharacterManagement;
